import dayjs, { type Dayjs } from 'dayjs';

import { DATE_FORMAT } from 'utils/constants';

/**
 * `rel:<amount><unit>`, unit D, W, M or Y (`rel:0D` is today). Saved table filters use these so
 * they stay relative to the day they are applied instead of freezing on the day they were saved.
 */
const RELATIVE_DATE_TOKEN_PATTERN = /^rel:(\d+)([DWMY])$/;

const RELATIVE_DATE_UNITS = {
  D: 'day',
  W: 'week',
  M: 'month',
  Y: 'year',
} as const;

type RelativeDateDirection = 'past' | 'future';

/**
 * Resolves a relative date token to `YYYY-MM-DD`, back (`past`) or forward (`future`) from today.
 * Other values pass through unchanged, so it works as a `serverSideFilterParsers` entry.
 */
export const resolveRelativeDateToken = (
  value: unknown,
  direction: RelativeDateDirection,
  today: Dayjs = dayjs()
): unknown => {
  if (typeof value !== 'string') {
    return value;
  }

  const match = value.match(RELATIVE_DATE_TOKEN_PATTERN);
  if (!match) {
    return value;
  }

  const amount = Number(match[1]);
  const unit = RELATIVE_DATE_UNITS[match[2] as keyof typeof RELATIVE_DATE_UNITS];
  const start = today.startOf('day');
  const resolved = direction === 'past' ? start.subtract(amount, unit) : start.add(amount, unit);

  return resolved.format(DATE_FORMAT);
};
